import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    IconButton,
    TextField,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Menu,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    MoreVert,
    ContentCopy,
    AutoAwesome,
    Search,
    FilterList,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const TemplatesManager = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCategory, setFilterCategory] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        content: '',
        category: 'other',
        platform: 'all',
    });

    // AI generation state
    const [aiContext, setAiContext] = useState('');
    const [aiTone, setAiTone] = useState('friendly');
    const [aiLanguage, setAiLanguage] = useState('ar');
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        fetchTemplates();
    }, [filterCategory, searchTerm]);

    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_BASE_URL}/api/templates`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { category: filterCategory, search: searchTerm },
            });
            setTemplates(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching templates:', error);
            setLoading(false);
        }
    };

    const handleCreateTemplate = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE_URL}/api/templates`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setDialogOpen(false);
            resetForm();
            fetchTemplates();
        } catch (error) {
            console.error('Error creating template:', error);
            alert('حدث خطأ أثناء إنشاء القالب');
        }
    };

    const handleGenerateWithAI = async () => {
        setAiLoading(true);
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.post(
                `${API_BASE_URL}/api/ai/reply-template`,
                {
                    context: aiContext,
                    tone: aiTone,
                    language: aiLanguage,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setFormData({
                ...formData,
                content: data.template,
            });
            setAiDialogOpen(false);
            setDialogOpen(true);
        } catch (error) {
            console.error('Error generating template:', error);
            alert('حدث خطأ أثناء توليد القالب');
        } finally {
            setAiLoading(false);
        }
    };

    const handleDeleteTemplate = async (id) => {
        if (!window.confirm('هل أنت متأكد من حذف هذا القالب؟')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE_URL}/api/templates/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            alert('حدث خطأ أثناء حذف القالب');
        }
    };

    const handleCopyTemplate = (content) => {
        navigator.clipboard.writeText(content);
        alert('تم نسخ القالب!');
    };

    const resetForm = () => {
        setFormData({
            name: '',
            content: '',
            category: 'other',
            platform: 'all',
        });
        setSelectedTemplate(null);
    };

    const getCategoryColor = (category) => {
        const colors = {
            greeting: 'success',
            support: 'info',
            sales: 'warning',
            followup: 'secondary',
            other: 'default',
        };
        return colors[category] || 'default';
    };

    const getCategoryLabel = (category) => {
        const labels = {
            greeting: 'ترحيب',
            support: 'دعم',
            sales: 'مبيعات',
            followup: 'متابعة',
            other: 'أخرى',
        };
        return labels[category] || category;
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: { xs: 2, md: 0 },
                mb: 3
            }}>
                <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                    قوالب الردود التلقائية
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                    <Button
                        variant="outlined"
                        startIcon={<AutoAwesome />}
                        onClick={() => setAiDialogOpen(true)}
                        fullWidth={true}
                        sx={{ flex: { xs: 1, md: 'initial' } }}
                    >
                        توليد بالذكاء الاصطناعي
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setDialogOpen(true)}
                        fullWidth={true}
                        sx={{ flex: { xs: 1, md: 'initial' } }}
                    >
                        قالب جديد
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        placeholder="بحث في القوالب..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        select
                        label="الفئة"
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                    >
                        <MenuItem value="">الكل</MenuItem>
                        <MenuItem value="greeting">ترحيب</MenuItem>
                        <MenuItem value="support">دعم</MenuItem>
                        <MenuItem value="sales">مبيعات</MenuItem>
                        <MenuItem value="followup">متابعة</MenuItem>
                        <MenuItem value="other">أخرى</MenuItem>
                    </TextField>
                </Grid>
            </Grid>

            {/* Templates Grid */}
            <Grid container spacing={3}>
                {templates.map((template) => (
                    <Grid item xs={12} md={6} key={template._id}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                                            {template.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                            <Chip
                                                label={getCategoryLabel(template.category)}
                                                size="small"
                                                color={getCategoryColor(template.category)}
                                            />
                                            {template.isAIGenerated && (
                                                <Chip
                                                    label="AI"
                                                    size="small"
                                                    icon={<AutoAwesome />}
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            setAnchorEl(e.currentTarget);
                                            setSelectedTemplate(template);
                                        }}
                                    >
                                        <MoreVert />
                                    </IconButton>
                                </Box>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        mb: 2,
                                        p: 2,
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                        maxHeight: 150,
                                        overflow: 'auto',
                                    }}
                                >
                                    {template.content}
                                </Typography>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        استخدم {template.usageCount} مرة
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<ContentCopy />}
                                        onClick={() => handleCopyTemplate(template.content)}
                                    >
                                        نسخ
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem
                    onClick={() => {
                        handleCopyTemplate(selectedTemplate.content);
                        setAnchorEl(null);
                    }}
                >
                    <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
                    <ListItemText>نسخ</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleDeleteTemplate(selectedTemplate._id);
                        setAnchorEl(null);
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>حذف</ListItemText>
                </MenuItem>
            </Menu>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>قالب جديد</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="اسم القالب"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="الفئة"
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            >
                                <MenuItem value="greeting">ترحيب</MenuItem>
                                <MenuItem value="support">دعم</MenuItem>
                                <MenuItem value="sales">مبيعات</MenuItem>
                                <MenuItem value="followup">متابعة</MenuItem>
                                <MenuItem value="other">أخرى</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="المنصة"
                                value={formData.platform}
                                onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                            >
                                <MenuItem value="all">الكل</MenuItem>
                                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                                <MenuItem value="telegram">Telegram</MenuItem>
                                <MenuItem value="instagram">Instagram</MenuItem>
                                <MenuItem value="facebook">Facebook</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                label="محتوى القالب"
                                placeholder="استخدم {name} للاسم، {product} للمنتج، إلخ..."
                                value={formData.content}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>إلغاء</Button>
                    <Button onClick={handleCreateTemplate} variant="contained">
                        حفظ
                    </Button>
                </DialogActions>
            </Dialog>

            {/* AI Generation Dialog */}
            <Dialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AutoAwesome sx={{ mr: 1, color: 'primary.main' }} />
                        توليد قالب بالذكاء الاصطناعي
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="السياق"
                                placeholder="مثال: رد على استفسار عن أسعار المنتجات"
                                value={aiContext}
                                onChange={(e) => setAiContext(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="الأسلوب"
                                value={aiTone}
                                onChange={(e) => setAiTone(e.target.value)}
                            >
                                <MenuItem value="friendly">ودود</MenuItem>
                                <MenuItem value="professional">احترافي</MenuItem>
                                <MenuItem value="formal">رسمي</MenuItem>
                                <MenuItem value="casual">غير رسمي</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="اللغة"
                                value={aiLanguage}
                                onChange={(e) => setAiLanguage(e.target.value)}
                            >
                                <MenuItem value="ar">العربية</MenuItem>
                                <MenuItem value="en">English</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAiDialogOpen(false)}>إلغاء</Button>
                    <Button
                        onClick={handleGenerateWithAI}
                        variant="contained"
                        disabled={aiLoading || !aiContext.trim()}
                        startIcon={aiLoading ? null : <AutoAwesome />}
                    >
                        {aiLoading ? 'جاري التوليد...' : 'توليد'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default TemplatesManager;
